import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, LogOut, LayoutGrid, Save, FileSpreadsheet, Trash2, Loader2,
  ChevronDown, ChevronRight, Video, Image as ImageIcon, FolderOpen,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { authApi } from "../admin/api";
import { plannerApi } from "../planner/api";
import { StrategyForm } from "../planner/StrategyForm";
import { AssetLibrary } from "../planner/AssetLibrary";
import { PieceEditor } from "../planner/PieceEditor";
import {
  STATUS_META, networkLabel, exportPlanXlsx, setPrefill,
} from "../planner/helpers";
import { loadImage } from "../studio/canvas";
import { usePageMeta } from "../hooks/usePageMeta";

const LOGO_SRC = "/brand-assets/daos-logo.png";
const PERSIST_LIMIT_CHARS = 7_000_000;

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

// Split the period into windows sized so each LLM call stays under the
// gateway timeout (~60s). Target ≈8 pieces per call.
function weeklyWindows(startISO, endISO, perWeek = 6) {
  const start = new Date(`${startISO}T00:00:00Z`);
  const end = new Date(`${endISO}T00:00:00Z`);
  if (isNaN(start) || isNaN(end) || end < start) return [];
  const span = Math.max(2, Math.min(7, Math.floor((7 * 8) / Math.max(perWeek, 1))));
  const out = [];
  let cur = start;
  while (cur <= end && out.length < 16) {
    const wEnd = new Date(cur);
    wEnd.setUTCDate(wEnd.getUTCDate() + (span - 1));
    const capped = wEnd > end ? end : wEnd;
    out.push({ start: cur.toISOString().slice(0, 10), end: capped.toISOString().slice(0, 10) });
    cur = new Date(capped);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

const defaultStrategy = () => ({
  start_date: todayISO(1),
  end_date: todayISO(28),
  networks: ["ig-feed", "ig-reels"],
  frequency_per_week: 3,
  static_percentage: 70,
  objective: "leads",
  theme: "",
  services: "",
  restrictions: "",
  background_mode: "mixed",
});

export default function Planner() {
  usePageMeta({ title: "Planner · Daos Ads" });
  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    const prev = tag?.getAttribute("content") || null;
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "robots"); document.head.appendChild(tag); }
    tag.setAttribute("content", "noindex,nofollow");
    return () => { if (prev === null) tag?.remove(); else tag?.setAttribute("content", prev); };
  }, []);

  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(defaultStrategy());
  const [assets, setAssets] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [planName, setPlanName] = useState("");
  const [planId, setPlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [logoImg, setLogoImg] = useState(null);

  // Session-only caches (not persisted): AI backgrounds + rendered arts per piece.
  const [bgCache, setBgCache] = useState({});
  const [renderCache, setRenderCache] = useState({});

  useEffect(() => { loadImage(LOGO_SRC).then(setLogoImg).catch(() => setLogoImg(null)); }, []);

  const refreshPlans = async () => {
    try {
      const d = await plannerApi.listPlans({ limit: 24 });
      setPlans(d.items || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refreshPlans(); }, []);

  const onGenerate = async () => {
    if (!strategy.theme.trim() || strategy.theme.trim().length < 3) {
      return toast.error("Describe el tema / eje del período");
    }
    const freq = Math.min(14, Math.max(1, Number(strategy.frequency_per_week) || 3));
    const windows = weeklyWindows(
      strategy.start_date,
      strategy.end_date,
      freq * Math.max(strategy.networks.length, 1),
    );
    if (!windows.length) return toast.error("Revisa el rango de fechas");

    setGenerating(true);
    setPieces([]);
    setExpanded(null);
    const acc = [];
    try {
      for (let i = 0; i < windows.length; i++) {
        const w = windows[i];
        setProgress(`Bloque ${i + 1} de ${windows.length}…`);
        const d = await plannerApi.generateMatrix({
          ...strategy,
          frequency_per_week: freq,
          start_date: w.start,
          end_date: w.end,
          theme: strategy.theme.trim(),
          services: strategy.services.trim() || null,
          restrictions: strategy.restrictions.trim() || null,
          asset_library: assets.map((a) => ({ id: a.id, name: a.name, type: a.type })),
          avoid_headlines: acc.map((p) => p.headline).filter(Boolean),
        });
        const list = (d.pieces || []).map((p) => ({ ...p, id: `${w.start}-${p.id}` }));
        acc.push(...list);
        setPieces([...acc]);
      }
      if (!acc.length) toast.error("La IA no devolvió piezas. Intenta de nuevo.");
      else toast.success(`${acc.length} piezas planificadas`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo generar la matriz completa";
      toast.error(acc.length ? `${msg} · se conservaron ${acc.length} piezas` : msg);
    } finally {
      setProgress("");
      setGenerating(false);
    }
  };

  const patchPiece = (id, patch) =>
    setPieces((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const filtered = useMemo(
    () => (filter === "all" ? pieces : pieces.filter((p) => p.status === filter)),
    [pieces, filter]
  );

  const counts = useMemo(() => ({
    all: pieces.length,
    pending: pieces.filter((p) => p.status === "pending").length,
    approved: pieces.filter((p) => p.status === "approved").length,
    rejected: pieces.filter((p) => p.status === "rejected").length,
  }), [pieces]);

  const onSavePlan = async () => {
    if (!pieces.length) return toast.error("Genera la matriz primero");
    const name = planName.trim() || `Plan ${strategy.start_date} → ${strategy.end_date}`;
    setSaving(true);
    try {
      // Persist asset previews only while under the safe document budget.
      let budget = PERSIST_LIMIT_CHARS;
      const assetPayload = assets.map((a) => {
        const size = (a.dataUrl || "").length;
        if (size <= budget) { budget -= size; return a; }
        return { id: a.id, name: a.name, type: a.type };
      });
      const payload = { name, strategy, pieces, asset_library: assetPayload };
      const saved = planId ? await plannerApi.updatePlan(planId, payload) : await plannerApi.savePlan(payload);
      setPlanId(saved.id);
      setPlanName(saved.name);
      toast.success(planId ? "Plan actualizado" : "Plan guardado");
      refreshPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el plan");
    } finally {
      setSaving(false);
    }
  };

  const onLoadPlan = async (id) => {
    try {
      const d = await plannerApi.getPlan(id);
      setPlanId(d.id);
      setPlanName(d.name || "");
      setStrategy({ ...defaultStrategy(), ...(d.strategy || {}) });
      setPieces(d.pieces || []);
      setAssets((d.asset_library || []).filter((a) => a.dataUrl));
      setBgCache({});
      setRenderCache({});
      setExpanded(null);
      toast.success(`Plan cargado · ${d.name}`);
    } catch (err) {
      toast.error("No se pudo cargar el plan");
    }
  };

  const onDeletePlan = async (id) => {
    try {
      await plannerApi.deletePlan(id);
      if (planId === id) { setPlanId(null); }
      toast.success("Plan eliminado");
      refreshPlans();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const openInVideo = (piece, backgroundDataUrl) => {
    setPrefill({
      target: "video",
      formatId: ["reels", "feed", "tiktok"].includes(piece.format) ? piece.format : "reels",
      duration: piece.duration || 15,
      headline: piece.headline || "",
      subtitle: piece.subtitle || "",
      cta: piece.cta || "Agenda una llamada",
      ctaColor: piece.cta_color || "gradient",
      gradientWords: piece.gradient_words || "",
      signalWords: piece.signal_words || "",
      signalColor: piece.signal_color || "amber",
      kenBurns: piece.video_config?.kenBurns || "zoom-in",
      glowPulse: piece.video_config?.glowPulse !== false,
      projectName: `${piece.date} · ${networkLabel(piece.network)}`,
      backgroundDataUrl: backgroundDataUrl || null,
    });
    navigate("/studiovideo");
  };

  const openInStudio = (piece, backgroundDataUrl) => {
    setPrefill({
      target: "art",
      templateId: piece.template,
      formatId: piece.format,
      headline: piece.headline || "",
      subtitle: piece.subtitle || "",
      cta: piece.cta || "",
      gradientWords: piece.gradient_words || "",
      signalWords: piece.signal_words || "",
      signalColor: piece.signal_color || "none",
      ctaColor: piece.cta_color || "gradient",
      imagePrompt: piece.background_prompt || "",
      backgroundDataUrl: backgroundDataUrl || null,
    });
    navigate("/studio");
  };

  const onLogout = () => { authApi.logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="planner-page">
      <Toaster theme="dark" position="top-right" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_SRC} alt="Daos Ads" className="h-9 w-auto" />
            <div className="hidden sm:block">
              <p className="text-sm font-extrabold tracking-tight leading-none">Planner</p>
              <p className="text-[11px] text-zinc-500">Matriz de contenido con IA</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/studio" data-testid="planner-nav-studio" className="text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">Studio</Link>
            <Link to="/studiovideo" data-testid="planner-nav-video" className="text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">Studio Video</Link>
            <Link to="/admin" data-testid="planner-nav-admin" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">
              <ChevronLeft className="h-3.5 w-3.5" /> CRM
            </Link>
            <button onClick={onLogout} data-testid="planner-logout" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">
              <LogOut className="h-3.5 w-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-5 py-8 grid lg:grid-cols-[380px_1fr] gap-6">
        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
          <StrategyForm value={strategy} onChange={setStrategy} onGenerate={onGenerate} loading={generating} progress={progress} />
          <AssetLibrary assets={assets} onChange={setAssets} />

          <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="h-4 w-4 text-[#FF8A00]" />
              <h2 className="font-display text-base font-extrabold tracking-tight">Planes guardados</h2>
            </div>
            <input
              data-testid="planner-plan-name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Nombre del plan"
              className="w-full rounded-xl bg-[#111111] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#FF8A00]/60 transition"
            />
            <div className="mt-3 flex gap-2">
              <button type="button" data-testid="planner-save-plan" onClick={onSavePlan} disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/10 px-3 py-2 text-xs font-semibold text-zinc-200 hover:text-white hover:border-white/25 disabled:opacity-40 transition">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar
              </button>
              <button type="button" data-testid="planner-export-xlsx"
                onClick={() => pieces.length ? exportPlanXlsx(pieces, planName || "plan-daos-ads") : toast.error("No hay piezas para exportar")}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/10 px-3 py-2 text-xs font-semibold text-zinc-200 hover:text-white hover:border-white/25 transition">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
              </button>
            </div>

            {plans.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="planner-plans-list">
                {plans.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2">
                    <button type="button" data-testid={`planner-load-${p.id}`} onClick={() => onLoadPlan(p.id)}
                      className="flex-1 text-left min-w-0">
                      <span className="block text-xs font-semibold text-white truncate">{p.name}</span>
                      <span className="block text-[10px] text-zinc-500">{(p.created_at || "").slice(0, 10)}</span>
                    </button>
                    <button type="button" data-testid={`planner-delete-${p.id}`} onClick={() => onDeletePlan(p.id)}
                      className="text-zinc-500 hover:text-rose-400 transition">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <section className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-[#FF8A00]" />
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Matriz de contenido</h1>
            </div>
            <div className="flex gap-2">
              {["all", "pending", "approved", "rejected"].map((k) => (
                <button key={k} type="button" data-testid={`planner-filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition ${
                    filter === k ? "border-[#FF8A00]/60 bg-[#FF8A00]/15 text-white" : "border-white/10 text-zinc-400 hover:text-white"
                  }`}>
                  {k === "all" ? "Todas" : STATUS_META[k].label} · {counts[k]}
                </button>
              ))}
            </div>
          </div>

          {pieces.length === 0 ? (
            <div data-testid="planner-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0A0A] p-16 text-center">
              <p className="text-lg font-extrabold tracking-tight">Aún no hay matriz</p>
              <p className="mt-2 text-sm text-zinc-500">
                Configura la estrategia, sube tus assets y genera el calendario con Claude Sonnet 4.5.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
              <table className="w-full text-sm" data-testid="planner-matrix-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="w-8" />
                    <th className="text-left px-3 py-3 font-semibold">Fecha</th>
                    <th className="text-left px-3 py-3 font-semibold">Red</th>
                    <th className="text-left px-3 py-3 font-semibold">Tipo</th>
                    <th className="text-left px-3 py-3 font-semibold hidden md:table-cell">Plantilla</th>
                    <th className="text-left px-3 py-3 font-semibold">Titular</th>
                    <th className="text-left px-3 py-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const open = expanded === p.id;
                    const st = STATUS_META[p.status] || STATUS_META.pending;
                    return (
                      <Fragment key={p.id}>
                        <tr data-testid={`planner-row-${p.id}`}
                          onClick={() => setExpanded(open ? null : p.id)}
                          className={`border-b border-white/5 cursor-pointer transition hover:bg-white/[0.03] ${open ? "bg-white/[0.04]" : ""}`}>
                          <td className="px-2 text-zinc-500">
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-zinc-300">{p.date}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-zinc-400 text-xs">{networkLabel(p.network)}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                              {p.type === "video" ? <Video className="h-3.5 w-3.5 text-[#FF8A00]" /> : <ImageIcon className="h-3.5 w-3.5 text-[#FFD23F]" />}
                              {p.type === "video" ? `Video ${p.duration || 15}s` : "Arte"}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-xs text-zinc-500 hidden md:table-cell">{p.template}</td>
                          <td className="px-3 py-3 text-zinc-100 font-medium max-w-[380px] truncate">{p.headline}</td>
                          <td className="px-3 py-3">
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-[#050505]">
                            <td colSpan={7} className="p-4">
                              <PieceEditor
                                piece={p}
                                assets={assets}
                                logoImg={logoImg}
                                bg={bgCache[p.id] || null}
                                onSetBg={(d) => setBgCache((c) => ({ ...c, [p.id]: d }))}
                                render={renderCache[p.id] || null}
                                onSetRender={(d) => setRenderCache((c) => ({ ...c, [p.id]: d }))}
                                onPatch={(patch) => patchPiece(p.id, patch)}
                                onOpenVideo={(bgUrl) => openInVideo(p, bgUrl)}
                                onOpenStudio={(bgUrl) => openInStudio(p, bgUrl)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
